import React from 'react';
import PropTypes from 'prop-types';

import { Tooltip } from '@sumologic/yama-ds-components';

import {
    iconLiteralSizes,
    tooltipLiteralPlacement
} from '~/modules/globalValues';

import Translate from '~/components/Translate/Translate';

// prettier-ignore
const TooltipIcon = props => {
    const {
        type,
        id,
        message,
        size,
        placement,
        delay,
        PopperProps,
        onClick = null,
        buttonAttrs,
        testId,
        className,
        suffix,
        variant,
        arrow,
        disableInteractive,
        iconComponent: IconComponent
    } = props;

    // Build the base icon element
    const baseIcon = IconComponent ? (
        <IconComponent
            className={['icon', 'icon-component', className].filter(Boolean).join(' ')}
            onClick={onClick}
            {...(testId && { 'data-sumo-test': testId })}
            {...(id && { id: id })}
        />
    ) : (
        <i
            className={['icon', `icon-${size}`, `icon-${type}`, className].filter(Boolean).join(' ')}
            onClick={onClick}
            {...(testId && { 'data-sumo-test': testId })}
            {...(id && { id: id })}
        />
    );

    // Wrap in span for MUI Tooltip ref/event forwarding
    // Note: While <i> and Icon component forward refs properly, the wrapper ensures:
    // 1. Consistent behavior across all tooltip use cases
    // 2. Handles edge cases (disabled elements, certain SVGs)
    // 3. d-inline-block maintains inline flow while supporting tooltip positioning
    const iconElem = message?.length ? (
        <span className="d-inline-block">
            {baseIcon}
        </span>
    ) : baseIcon;

    // Handle button wrapping if buttonAttrs provided
    // Wrapper is REQUIRED here because disabled buttons don't fire mouse events
    const finalElement = buttonAttrs && !onClick ? (
        <span className="d-inline-block">
            <button {...buttonAttrs}>
                {baseIcon}
            </button>
        </span>
    ) : iconElem;

    // Convert Bootstrap delay object to MUI props
    const enterDelay = delay?.show !== undefined ? delay.show : 0;
    const leaveDelay = delay?.hide !== undefined ? delay.hide : 0;

    return message?.length ? (
        <Tooltip
            title={<Translate message={message} />}
            placement={placement}
            enterDelay={enterDelay}
            leaveDelay={leaveDelay}
            PopperProps={PopperProps}
            variant={variant}
            arrow={arrow}
            disableInteractive={disableInteractive}
            classes={{
                tooltip: `tooltip-icon ${suffix}`
            }}
        >
            {finalElement}
        </Tooltip>
    ) : (
        finalElement
    );
};

TooltipIcon.propTypes = {
    size: PropTypes.oneOf(iconLiteralSizes),
    placement: PropTypes.oneOf(tooltipLiteralPlacement),
    PopperProps: PropTypes.object,
    buttonAttrs: PropTypes.object,
    delay: PropTypes.object,
    type: PropTypes.string.isRequired,
    className: PropTypes.string,
    testId: PropTypes.string,
    message: PropTypes.string,
    suffix: PropTypes.string,
    id: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'error']),
    iconComponent: PropTypes.elementType,
    onClick: PropTypes.func,
    arrow: PropTypes.bool,
    disableInteractive: PropTypes.bool
};

TooltipIcon.defaultProps = {
    size: iconLiteralSizes[1], // sm|md*|lg|xl,
    placement: tooltipLiteralPlacement[1], // auto|left*|right|top|bottom
    delay: { show: 250, hide: 0 },
    PopperProps: {
        modifiers: [
            {
                name: 'flip',
                enabled: true
            }
        ]
    },
    suffix: 'standard',
    variant: 'default',
    arrow: true,
    disableInteractive: true
};

export default TooltipIcon;
